import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Database, 
  Search, 
  Users, 
  BarChart3, 
  Smartphone, 
  Shield, 
  Wifi, 
  BookOpen, 
  Monitor,
  Activity,
  Server,
  ChevronRight,
  CheckCircle2,
  Code,
  Brain,
  Network,
  Terminal,
  Layers
} from 'lucide-react'

interface SystemModule {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  category: string
  realStats: {
    label: string
    value: string
    unit: string
    description: string
  }[]
  keyFeatures: string[]
  technicalSpecs: {
    architecture: string
    database: string
    performance: string
    security: string
  }
}

const SoftwareShowcase: React.FC = () => {
  const { t } = useTranslation()
  const [currentSystemIndex, setCurrentSystemIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: false, margin: "-20%" })

  // 真实的系统数据，基于实际图书馆业务
  const systemsData = t('softwareShowcase.systems', { returnObjects: true }) as any[]
  const systemModules: SystemModule[] = systemsData?.map((system: any, index: number) => ({
    ...system,
    icon: [
      <Database className="w-8 h-8" />,
      <Wifi className="w-8 h-8" />,
      <Search className="w-8 h-8" />,
      <Users className="w-8 h-8" />,
      <BarChart3 className="w-8 h-8" />,
      <BookOpen className="w-8 h-8" />,
      <Shield className="w-8 h-8" />,
      <Smartphone className="w-8 h-8" />,
      <Monitor className="w-8 h-8" />
    ][index]
  })) || [
    {
      id: 'clusterManagement',
      name: '图书馆集群管理系统',
      description: '统一管理多个图书馆分馆的核心平台，实现资源共享与协同管理',
      icon: <Database className="w-8 h-8" />,
      category: '核心管理',
      realStats: [
        { label: '管理分馆数量', value: '32', unit: '个', description: '覆盖全省图书馆网络' },
        { label: '日均处理事务', value: '15,680', unit: '笔', description: '借还、查询、预约等业务' },
        { label: '并发支持用户', value: '2,000', unit: '人', description: '高峰期同时在线用户' },
        { label: '数据同步延迟', value: '< 50', unit: 'ms', description: '实时数据同步保障' }
      ],
      keyFeatures: [
        '多馆统一用户认证',
        '跨馆图书借还',
        '统一资源编目',
        '分级权限管理',
        '实时数据同步',
        '集中运维监控'
      ],
      technicalSpecs: {
        architecture: '分布式微服务架构',
        database: 'MySQL主从集群 + Redis缓存',
        performance: '支持千万级图书数据',
        security: 'OAuth2.0 + JWT双重认证'
      }
    },
    {
      id: 'tagConversion',
      name: 'RFID标签转换系统',
      description: '批量处理图书RFID标签数据转换，支持多种标签格式兼容',
      icon: <Wifi className="w-8 h-8" />,
      category: '数据处理',
      realStats: [
        { label: '日转换标签', value: '25,000', unit: '张', description: '支持批量高速处理' },
        { label: '转换成功率', value: '99.8', unit: '%', description: '行业领先的准确率' },
        { label: '处理速度', value: '800', unit: '张/分钟', description: '高效批量处理能力' },
        { label: '支持标签类型', value: '15', unit: '种', description: '兼容主流RFID标签' }
      ],
      keyFeatures: [
        '多格式标签兼容',
        '批量数据验证',
        '错误自动修复',
        '处理进度监控',
        '历史记录追踪',
        'API接口开放'
      ],
      technicalSpecs: {
        architecture: '高并发处理引擎',
        database: 'PostgreSQL + 时序数据库',
        performance: '支持百万级标签处理',
        security: 'AES-256数据加密'
      }
    },
    {
      id: 'opacSearch',
      name: 'OPAC检索系统',
      description: '智能化图书检索系统，提供多维度搜索和个性化推荐服务',
      icon: <Search className="w-8 h-8" />,
      category: '检索服务',
      realStats: [
        { label: '日检索请求', value: '45,600', unit: '次', description: '读者日均查询量' },
        { label: '平均响应时间', value: '120', unit: 'ms', description: '毫秒级检索响应' },
        { label: '检索准确率', value: '97.2', unit: '%', description: '智能算法优化结果' },
        { label: '支持语言', value: '12', unit: '种', description: '多语言检索支持' }
      ],
      keyFeatures: [
        '智能语义搜索',
        '多字段联合检索',
        '个性化推荐',
        '搜索历史记录',
        '热门搜索统计',
        '移动端优化'
      ],
      technicalSpecs: {
        architecture: 'ElasticSearch搜索引擎',
        database: '全文索引 + 关系数据库',
        performance: '千万级图书秒级检索',
        security: 'HTTPS + 防注入攻击'
      }
    },
    {
      id: 'selfService',
      name: '自助服务系统',
      description: '读者自助借还、办证、续借等服务的综合管理平台',
      icon: <Users className="w-8 h-8" />,
      category: '读者服务',
      realStats: [
        { label: '日自助服务', value: '12,400', unit: '笔', description: '借还、续借、办证等' },
        { label: '自助服务率', value: '85.6', unit: '%', description: '减轻人工服务压力' },
        { label: '平均服务时间', value: '35', unit: '秒', description: '高效便民服务' },
        { label: '支持认证方式', value: '6', unit: '种', description: '多元化身份验证' }
      ],
      keyFeatures: [
        '人脸识别认证',
        '多卡类支持',
        '自助办证服务',
        '移动端续借',
        '服务状态监控',
        '多语言界面'
      ],
      technicalSpecs: {
        architecture: 'RESTful API架构',
        database: 'MySQL + 缓存优化',
        performance: '支持高并发访问',
        security: '生物识别 + 多重验证'
      }
    },
    {
      id: 'dataVisualization',
      name: '数据可视化系统',
      description: '图书馆运营数据的实时监控、分析和可视化展示平台',
      icon: <BarChart3 className="w-8 h-8" />,
      category: '数据分析',
      realStats: [
        { label: '监控数据点', value: '500+', unit: '个', description: '全方位业务数据监控' },
        { label: '报表更新频率', value: '5', unit: '分钟', description: '准实时数据更新' },
        { label: '数据准确率', value: '99.9', unit: '%', description: '高质量数据保障' },
        { label: '支持图表类型', value: '25', unit: '种', description: '丰富的可视化形式' }
      ],
      keyFeatures: [
        '实时数据监控',
        '多维度分析',
        '自定义报表',
        '趋势预测分析',
        '异常数据告警',
        '移动端查看'
      ],
      technicalSpecs: {
        architecture: '大数据处理平台',
        database: 'ClickHouse + Redis',
        performance: '亿级数据实时分析',
        security: '数据脱敏 + 访问控制'
      }
    },
    {
      id: 'rfidInventory',
      name: 'RFID盘点系统',
      description: '基于RFID技术的智能图书盘点系统，支持移动和固定盘点',
      icon: <BookOpen className="w-8 h-8" />,
      category: '库存管理',
      realStats: [
        { label: '盘点效率', value: '1,200', unit: '册/小时', description: 'RFID批量快速盘点' },
        { label: '盘点准确率', value: '99.95', unit: '%', description: '精确库存管理' },
        { label: '遗失图书发现', value: '98.5', unit: '%', description: '有效减少图书损失' },
        { label: '盘点周期', value: '7', unit: '天', description: '定期自动盘点' }
      ],
      keyFeatures: [
        'RFID批量识别',
        '移动盘点终端',
        '智能路径规划',
        '异常图书定位',
        '盘点报告生成',
        '历史对比分析'
      ],
      technicalSpecs: {
        architecture: 'RFID + 移动计算',
        database: '位置数据库 + 日志系统',
        performance: '万册图书快速盘点',
        security: 'RFID防伪 + 数据校验'
      }
    },
    {
      id: 'accessControl',
      name: '门禁安防系统',
      description: '智能门禁管理和安全监控系统，保障图书馆安全运行',
      icon: <Shield className="w-8 h-8" />,
      category: '安全管理',
      realStats: [
        { label: '日通行记录', value: '8,600', unit: '人次', description: '读者进出统计' },
        { label: '安全事件', value: '< 0.1', unit: '%', description: '极低的安全事故率' },
        { label: '识别准确率', value: '99.7', unit: '%', description: '高精度身份识别' },
        { label: '响应时间', value: '< 1', unit: '秒', description: '快速通行体验' }
      ],
      keyFeatures: [
        '多重身份验证',
        '实时人流统计',
        '异常行为检测',
        '视频监控联动',
        '紧急疏散管理',
        '访客管理系统'
      ],
      technicalSpecs: {
        architecture: '物联网 + AI识别',
        database: '时序数据 + 关系数据库',
        performance: '毫秒级识别响应',
        security: '多层安全防护体系'
      }
    },
    {
      id: 'mobileLibrary',
      name: '手机图书馆',
      description: '基于移动端的图书馆服务平台，随时随地享受便民服务',
      icon: <Smartphone className="w-8 h-8" />,
      category: '移动服务',
      realStats: [
        { label: '月活跃用户', value: '28,500', unit: '人', description: '移动端活跃读者' },
        { label: '日服务请求', value: '15,200', unit: '次', description: '查询、续借、预约等' },
        { label: '服务满意度', value: '4.8', unit: '/5分', description: '读者评价反馈' },
        { label: '功能模块', value: '15', unit: '个', description: '全方位移动服务' }
      ],
      keyFeatures: [
        '手机一键登录',
        '图书搜索预约',
        '在线续借还书',
        '活动报名参与',
        '消息推送提醒',
        '个人借阅历史'
      ],
      technicalSpecs: {
        architecture: '移动应用 + 云服务',
        database: '云数据库 + CDN加速',
        performance: '支持高并发访问',
        security: '多重认证 + 数据加密'
      }
    },
    {
      id: 'rfidPlatform',
      name: 'RFID设备管理平台',
      description: '统一管理和监控所有RFID设备的运行状态和性能指标',
      icon: <Monitor className="w-8 h-8" />,
      category: '设备管理',
      realStats: [
        { label: '管理设备数', value: '156', unit: '台', description: '全馆RFID设备统一管理' },
        { label: '设备在线率', value: '98.9', unit: '%', description: '高可用性保障' },
        { label: '故障预警', value: '95.2', unit: '%', description: '提前发现设备问题' },
        { label: '维护响应时间', value: '< 30', unit: '分钟', description: '快速故障处理' }
      ],
      keyFeatures: [
        '设备状态监控',
        '故障自动告警',
        '性能数据分析',
        '远程配置管理',
        '维护计划管理',
        '设备生命周期'
      ],
      technicalSpecs: {
        architecture: '物联网管理平台',
        database: '时序数据库 + 监控系统',
        performance: '千台设备实时监控',
        security: '设备认证 + 通信加密'
      }
    }
  ]

  // 滚动翻页效果 - 支持鼠标悬浮暂停
  const [isPaused, setIsPaused] = useState(false)
  
  useEffect(() => {
    if (!isInView || isPaused) return

    const interval = setInterval(() => {
      setCurrentSystemIndex((prev) => (prev + 1) % systemModules.length)
    }, 8000) // 每8秒切换一个系统，慢一些

    return () => clearInterval(interval)
  }, [isInView, systemModules.length, isPaused])

  const currentSystem = systemModules[currentSystemIndex]

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* 轻量背景装饰 */}
      <div className="absolute inset-0">
        {/* 微妙的网格背景 */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* 轻柔的光效 */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* 头部标题和系统选择区域 */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${isExpanded ? "mb-16" : "mb-8"}`}>
          {/* 左侧：标题和介绍 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center px-6 py-3 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
              <Brain className="w-5 h-5 mr-2" />
              <span>{t('softwareShowcase.badge')}</span>
            </div>
            
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight text-gray-900">
                {t('softwareShowcase.title.main')}
                <span className="block text-blue-600">
                  {t('softwareShowcase.title.sub')}
                </span>
              </h2>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {t('softwareShowcase.description')}
              </p>
              
              {/* 收起按钮 - 仅在展开时显示 */}
              {isExpanded && (
                <motion.button
                  onClick={() => setIsExpanded(false)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                  className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 shadow-sm hover:shadow-md group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="mr-2">{t('softwareShowcase.collapseButton')}</span>
                  <motion.div
                    animate={{ rotate: [0, -180, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ChevronRight className="w-5 h-5 transform rotate-90" />
                  </motion.div>
                </motion.button>
              )}
            </div>
            

          </motion.div>

          {/* 右侧：系统选择区域 - 仅在展开时显示 */}
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, x: 30, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 30, scale: 0.95 }}
              transition={{ duration: 0.6, type: "spring" }}
              className="relative"
            >
              {/* 背景装饰 */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-blue-50/30 rounded-3xl blur-xl"></div>
              
              <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 border border-blue-200/50 shadow-xl shadow-blue-100/20">
                <div className="grid grid-cols-3 gap-3">
                  {systemModules.map((system, index) => (
                    <motion.button
                      key={system.id}
                      onClick={() => setCurrentSystemIndex(index)}
                      className={`group relative overflow-hidden rounded-2xl transition-all duration-500 ${
                        index === currentSystemIndex 
                          ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-lg shadow-blue-600/25' 
                          : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                      whileHover={{ 
                        scale: 1.03, 
                        y: -2,
                        transition: { type: "spring", stiffness: 400, damping: 17 }
                      }}
                      whileTap={{ scale: 0.97 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                    >
                      <div className="relative p-3">
                        {/* 图标区域 */}
                        <div className="flex items-center justify-center mb-2">
                          <motion.div 
                            className={`p-2 rounded-lg transition-all duration-500 ${
                              index === currentSystemIndex 
                                ? 'bg-white/20 text-white' 
                                : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
                            }`}
                          >
                            {React.cloneElement(system.icon as React.ReactElement, {
                              className: "w-5 h-5"
                            })}
                          </motion.div>
                        </div>
                        
                        {/* 文字信息 */}
                        <div className="text-center space-y-1">
                          <div className={`font-semibold text-xs leading-tight transition-colors duration-500 ${
                            index === currentSystemIndex 
                              ? 'text-white' 
                              : 'text-gray-900 group-hover:text-blue-700'
                          }`}>
                            {system.name}
                          </div>
                          <div className={`text-xs transition-colors duration-500 ${
                            index === currentSystemIndex 
                              ? 'text-blue-100' 
                              : 'text-gray-500 group-hover:text-blue-600'
                          }`}>
                            {system.category}
                          </div>
                        </div>
                      </div>
                      
                      {/* 选中状态的进度条 */}
                      {index === currentSystemIndex && (
                        <motion.div
                          className="absolute bottom-0 left-0 h-1 bg-white/50 rounded-b-2xl"
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 8, ease: "linear" }}
                          key={currentSystemIndex}
                        />
                      )}
                    </motion.button>
                  ))}
                </div>
                
              </div>
            </motion.div>
          )}
          
           {/* 未展开时的引导按钮区域 */}
           {!isExpanded && (
             <motion.button
               onClick={() => setIsExpanded(true)}
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.6 }}
               className="group relative w-full text-center p-12 bg-gradient-to-br from-blue-50 to-blue-100/80 rounded-3xl border-2 border-dashed border-blue-300 hover:border-blue-500 hover:from-blue-100 hover:to-blue-200/80 shadow-lg hover:shadow-xl transition-all duration-500"
               whileHover={{ scale: 1.02, y: -4 }}
               whileTap={{ scale: 0.98 }}
             >
               {/* 背景光效 */}
               <motion.div
                 className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-3xl"
                 animate={{ 
                   x: ['-100%', '100%'],
                   opacity: [0, 0.6, 0]
                 }}
                 transition={{ 
                   duration: 3, 
                   repeat: Infinity,
                   ease: "easeInOut"
                 }}
               />
               
               
               {/* 左右布局：文字 + 鼠标图标 */}
               <div className="flex items-center justify-between w-full">
                 {/* 左侧：引导文案 */}
                 <div className="flex-1 text-left">
                   <p className="text-2xl font-bold text-gray-900 mb-2">{t('softwareShowcase.exploreButton.title')}</p>
                   <p className="text-lg text-gray-600">{t('softwareShowcase.exploreButton.subtitle')}</p>
                 </div>
                 
                 {/* 右侧：鼠标样式箭头 */}
                 <div className="flex items-center justify-center ml-8">
                   <motion.div
                     className="relative"
                     animate={{ 
                       y: [0, -15, 0],
                       rotate: [0, 10, -10, 0]
                     }}
                     transition={{ 
                       duration: 2.5, 
                       repeat: Infinity,
                       ease: "easeInOut"
                     }}
                   >
                     <svg width="64" height="76" viewBox="0 0 64 76" fill="none" className="text-blue-600 drop-shadow-xl">
                       {/* 鼠标主体 */}
                       <motion.path
                         d="M32 6C23 6 16 13 16 22V42C16 51 23 58 32 58C41 58 48 51 48 42V22C48 13 41 6 32 6Z"
                         fill="currentColor"
                         fillOpacity="0.95"
                         animate={{ 
                           fillOpacity: [0.8, 1, 0.8],
                           scale: [1, 1.08, 1]
                         }}
                         transition={{ 
                           duration: 2, 
                           repeat: Infinity,
                           ease: "easeInOut"
                         }}
                       />
                       {/* 鼠标分割线 */}
                       <path
                         d="M32 6V26"
                         stroke="white"
                         strokeWidth="3"
                         strokeLinecap="round"
                       />
                       {/* 滚轮指示 */}
                       <motion.circle
                         cx="32"
                         cy="17"
                         r="3.5"
                         fill="white"
                         animate={{ 
                           scale: [1, 1.5, 1],
                           opacity: [0.8, 1, 0.8]
                         }}
                         transition={{ 
                           duration: 1.5, 
                           repeat: Infinity,
                           ease: "easeInOut"
                         }}
                       />
                       {/* 箭头指针 */}
                       <motion.path
                         d="M32 62L40 66L35 70L32 68L29 70L24 66L32 62Z"
                         fill="currentColor"
                         animate={{ 
                           y: [0, 6, 0],
                           scale: [1, 1.3, 1]
                         }}
                         transition={{ 
                           duration: 1.8, 
                           repeat: Infinity,
                           ease: "easeInOut"
                         }}
                       />
                     </svg>
                     
                     {/* 点击效果圆环 */}
                     <motion.div
                       className="absolute -inset-6 border-4 border-blue-500/60 rounded-full"
                       animate={{ 
                         scale: [1, 1.8, 1],
                         opacity: [0, 0.9, 0]
                       }}
                       transition={{ 
                         duration: 2.5, 
                         repeat: Infinity,
                         ease: "easeOut"
                       }}
                     />
                     
                     {/* 额外的强调圆环 */}
                     <motion.div
                       className="absolute -inset-8 border-3 border-blue-400/40 rounded-full"
                       animate={{ 
                         scale: [1, 2.2, 1],
                         opacity: [0.8, 0.1, 0.8]
                       }}
                       transition={{ 
                         duration: 3.5, 
                         repeat: Infinity,
                         ease: "easeOut",
                         delay: 0.8
                       }}
                     />
                   </motion.div>
                 </div>
               </div>
               
               {/* 外围呼吸光环 */}
               <motion.div
                 className="absolute inset-0 rounded-3xl border-2 border-blue-400/30"
                 animate={{ 
                   scale: [1, 1.02, 1],
                   opacity: [0.5, 0.8, 0.5]
                 }}
                 transition={{ 
                   duration: 3, 
                   repeat: Infinity,
                   ease: "easeInOut"
                 }}
               />
             </motion.button>
           )}
        </div>

         {/* 主要展示区域 - 仅在展开时显示 */}
         {isExpanded && (
           <AnimatePresence mode="wait">
           <motion.div
             key={currentSystemIndex}
             initial={{ opacity: 0, x: 100, rotateY: -15 }}
             animate={{ opacity: 1, x: 0, rotateY: 0 }}
             exit={{ opacity: 0, x: -100, rotateY: 15 }}
             transition={{ duration: 0.8, ease: "easeInOut" }}
             className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-200/50 overflow-hidden"
             style={{ perspective: '1000px' }}
             onMouseEnter={() => setIsPaused(true)}
             onMouseLeave={() => setIsPaused(false)}
           >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
              {/* 左侧：系统信息 */}
              <div className="lg:col-span-2 p-8 lg:p-12 bg-gradient-to-br from-blue-50 to-blue-100/50">
                {/* 系统头部 */}
                <div className="flex items-center space-x-4 mb-8">
                  <motion.div 
                    className="relative p-4 bg-blue-600 text-white rounded-2xl shadow-lg"
                    animate={{ 
                      boxShadow: [
                        '0 0 20px rgba(59, 130, 246, 0.3)',
                        '0 0 30px rgba(147, 51, 234, 0.4)',
                        '0 0 20px rgba(59, 130, 246, 0.3)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {currentSystem.icon}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </motion.div>
                  <div>
                    <div className="text-sm font-medium text-blue-600 mb-1 font-mono">[{currentSystem.category}]</div>
                    <h3 className="text-2xl font-bold text-gray-900">{currentSystem.name}</h3>
                  </div>
                </div>
                
                {/* 实时状态指示器 */}
                <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600 font-mono">STATUS</span>
                    <span className="text-green-600 font-mono">OPERATIONAL</span>
                  </div>
                  <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-green-500"
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 1.5, delay: 0.5 }}
                    />
                  </div>
                </div>
                
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {currentSystem.description}
                </p>

                {/* 核心功能 */}
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Terminal className="w-5 h-5 text-blue-600 mr-2" />
                    {t('softwareShowcase.keyFeaturesTitle')}
                  </h4>
                  {currentSystem.keyFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center space-x-3 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-300"
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-gray-700 font-medium font-mono text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>
                
                {/* 代码示例 */}
                <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Code className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-600 text-sm font-mono">system.api.status</span>
                  </div>
                  <div className="font-mono text-xs text-gray-700">
                    <div>{'{'}</div>
                    <div className="ml-4">
                      <span className="text-blue-600">"system"</span>: <span className="text-green-600">"{currentSystem.name}"</span>,
                    </div>
                    <div className="ml-4">
                      <span className="text-blue-600">"status"</span>: <span className="text-green-600">"active"</span>,
                    </div>
                    <div className="ml-4">
                      <span className="text-blue-600">"uptime"</span>: <span className="text-blue-600">99.9</span>
                    </div>
                    <div>{'}'}</div>
                  </div>
                </div>
              </div>

              {/* 右侧：数据展示 */}
              <div className="lg:col-span-3 p-8 lg:p-12 relative">
                {/* 数字雨背景效果 */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                  <div className="text-blue-300 font-mono text-xs animate-pulse">
                    01001100<br/>11010010<br/>00110101<br/>10101100
                  </div>
                </div>
                
                {/* 实时监控面板 */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xl font-bold text-gray-900 flex items-center">
                      <Activity className="w-6 h-6 text-green-500 mr-2 animate-pulse" />
                      {t('softwareShowcase.realTimeMonitorTitle')}
                    </h4>
                    <div className="flex items-center space-x-2 text-green-600 text-sm font-mono">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>LIVE</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {currentSystem.realStats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20, rotateX: -15 }}
                        animate={{ opacity: 1, y: 0, rotateX: 0 }}
                        transition={{ delay: 0.4 + index * 0.1, type: "spring" }}
                        className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-blue-200 hover:border-blue-400 transition-all duration-300 group shadow-lg"
                        whileHover={{ scale: 1.02, y: -2 }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-2xl font-bold font-mono">
                            <span className="text-blue-600">
                              {stat.value}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">{stat.unit}</span>
                          </div>
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse group-hover:animate-ping"></div>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 mb-2">{stat.label}</div>
                        <div className="text-xs text-gray-600">{stat.description}</div>
                        
                        {/* 动态进度条 */}
                        <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div 
                            className="h-full bg-blue-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, parseInt(stat.value.replace(/[^0-9]/g, '')) / 100 * 100)}%` }}
                            transition={{ duration: 2, delay: 0.5 + index * 0.2 }}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* 技术架构终端 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="bg-gray-50 p-6 rounded-2xl border border-gray-200 font-mono shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Terminal className="w-5 h-5 mr-2 text-blue-600" />
                      {t('softwareShowcase.techSpecsTitle')}
                    </h4>
                    <div className="flex space-x-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex">
                      <span className="text-blue-600 w-20">$</span>
                      <span className="text-gray-900">architecture:</span>
                      <span className="text-blue-700 ml-2">{currentSystem.technicalSpecs.architecture}</span>
                    </div>
                    <div className="flex">
                      <span className="text-blue-600 w-20">$</span>
                      <span className="text-gray-900">database:</span>
                      <span className="text-blue-700 ml-2">{currentSystem.technicalSpecs.database}</span>
                    </div>
                    <div className="flex">
                      <span className="text-blue-600 w-20">$</span>
                      <span className="text-gray-900">performance:</span>
                      <span className="text-blue-700 ml-2">{currentSystem.technicalSpecs.performance}</span>
                    </div>
                    <div className="flex">
                      <span className="text-blue-600 w-20">$</span>
                      <span className="text-gray-900">security:</span>
                      <span className="text-blue-700 ml-2">{currentSystem.technicalSpecs.security}</span>
                    </div>
                    <div className="flex items-center mt-4">
                      <span className="text-blue-600 w-20">$</span>
                      <span className="text-green-600">system --status</span>
                      <motion.span 
                        className="text-green-600 ml-2"
                        animate={{ opacity: [1, 0, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        ●
                      </motion.span>
                      <span className="text-green-400 ml-1">RUNNING</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
          </AnimatePresence>
        )}

         {/* 底部技术实力展示 */}
         <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
           transition={{ duration: 0.8, delay: 1.2 }}
           className={isExpanded ? "mt-20" : "mt-12"}
         >
           <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-10 border border-blue-200/50 shadow-xl shadow-blue-100/20">
             {/* 标题区域 */}
             <div className="text-center mb-12">
               <h3 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center">
                 <Layers className="w-8 h-8 text-blue-600 mr-3" />
                 {t('softwareShowcase.techStrength.title')}
               </h3>
               <p className="text-lg text-gray-600">{t('softwareShowcase.techStrength.description')}</p>
             </div>
             
             {/* 数据统计区域 */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
               <motion.div 
                 className="text-center group"
                 whileHover={{ scale: 1.05, y: -5 }}
                 transition={{ type: "spring", stiffness: 300 }}
               >
                 <div className="relative mb-6">
                   <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl flex items-center justify-center mx-auto border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-lg transition-all duration-300">
                     <Code className="w-12 h-12 text-blue-600" />
                   </div>
                   <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                     <span className="text-white text-sm font-bold">52</span>
                   </div>
                 </div>
                 <div className="text-4xl font-bold font-mono text-blue-600 mb-2">52+</div>
                 <div className="text-gray-700 font-semibold">{t('softwareShowcase.techStrength.stats.copyrights')}</div>
               </motion.div>
               
               <motion.div 
                 className="text-center group"
                 whileHover={{ scale: 1.05, y: -5 }}
                 transition={{ type: "spring", stiffness: 300 }}
               >
                 <div className="relative mb-6">
                   <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl flex items-center justify-center mx-auto border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-lg transition-all duration-300">
                     <Brain className="w-12 h-12 text-blue-600" />
                   </div>
                   <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                     <span className="text-white text-sm font-bold">18</span>
                   </div>
                 </div>
                 <div className="text-4xl font-bold font-mono text-blue-600 mb-2">18+</div>
                 <div className="text-gray-700 font-semibold">{t('softwareShowcase.techStrength.stats.patents')}</div>
               </motion.div>
               
               <motion.div 
                 className="text-center group"
                 whileHover={{ scale: 1.05, y: -5 }}
                 transition={{ type: "spring", stiffness: 300 }}
               >
                 <div className="relative mb-6">
                   <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl flex items-center justify-center mx-auto border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-lg transition-all duration-300">
                     <Server className="w-12 h-12 text-blue-600" />
                   </div>
                   <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                     <CheckCircle2 className="w-4 h-4 text-white" />
                   </div>
                 </div>
                 <div className="text-4xl font-bold font-mono text-blue-600 mb-2">99.8%</div>
                 <div className="text-gray-700 font-semibold">{t('softwareShowcase.techStrength.stats.stability')}</div>
               </motion.div>
               
               <motion.div 
                 className="text-center group"
                 whileHover={{ scale: 1.05, y: -5 }}
                 transition={{ type: "spring", stiffness: 300 }}
               >
                 <div className="relative mb-6">
                   <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl flex items-center justify-center mx-auto border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-lg transition-all duration-300">
                     <Network className="w-12 h-12 text-blue-600" />
                   </div>
                   <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                     <span className="text-white text-sm font-bold">10</span>
                   </div>
                 </div>
                 <div className="text-4xl font-bold font-mono text-blue-600 mb-2">10+</div>
                 <div className="text-gray-700 font-semibold">{t('softwareShowcase.techStrength.stats.experience')}</div>
               </motion.div>
             </div>
             
             {/* 分割线 */}
             <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent mb-10"></div>
             
             {/* 技术标签区域 */}
             <div className="text-center">
               <h4 className="text-lg font-semibold text-gray-800 mb-6">{t('softwareShowcase.techStrength.techStackTitle')}</h4>
               <div className="flex flex-wrap justify-center gap-4">
                 {(t('softwareShowcase.techStrength.techStack', { returnObjects: true }) as string[]).map((tech: string, index: number) => (
                   <motion.span
                     key={tech}
                     initial={{ opacity: 0, scale: 0 }}
                     animate={{ opacity: 1, scale: 1 }}
                     transition={{ delay: 1.5 + index * 0.1 }}
                     className="px-6 py-3 bg-blue-50 text-blue-700 border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-100 hover:scale-105 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer"
                     whileHover={{ y: -2 }}
                   >
                     {tech}
                   </motion.span>
                 ))}
               </div>
             </div>
           </div>
         </motion.div>
      </div>
    </section>
  )
}

export default SoftwareShowcase